// Chat types mirroring the mobileserver enums (MessageType / MessageStatus / MessageSender).

export type ChatMessageType =
    | 'TEXT'
    | 'ROAD_SITUATION'
    | 'TECHNICAL_ISSUE'
    | 'MEDICAL_EMERGENCY'
    | 'ACCIDENT_DTP'
    | 'URGENT_CALL'

export type ChatMessageStatus = 'SENT' | 'DELIVERED' | 'READ'

export type ChatSender = 'DRIVER' | 'DISPATCHER'

export interface ChatMessage {
    id: string
    serverId?: string
    clientMessageId?: string
    message: string
    sender: ChatSender | string
    messageType?: ChatMessageType | string
    messageTypeDisplay?: string
    date: string | number
    isRead?: boolean
    status?: ChatMessageStatus | string
    name?: string
    driverId?: number
    routeId?: number
}

// Message type metadata: label, icon and urgency level (drives the bubble colour).
export interface MessageTypeMeta {
    value: ChatMessageType
    label: string
    icon: string
    severity: 'normal' | 'warn' | 'danger'
}

// Ordered from the most urgent one, matching the priority in mobileserver.
export const MESSAGE_TYPES: MessageTypeMeta[] = [
    {value: 'URGENT_CALL', label: 'ТЕРМІНОВИЙ ВИКЛИК', icon: '⚠️', severity: 'danger'},
    {value: 'ACCIDENT_DTP', label: 'Аварія / ДТП', icon: '🚗', severity: 'danger'},
    {value: 'MEDICAL_EMERGENCY', label: 'Медичний випадок', icon: '🚑', severity: 'danger'},
    {value: 'TECHNICAL_ISSUE', label: 'Технічна несправність', icon: '🛠️', severity: 'warn'},
    {value: 'ROAD_SITUATION', label: 'Дорожня ситуація', icon: '🚧', severity: 'warn'},
    {value: 'TEXT', label: 'Текстове повідомлення', icon: '', severity: 'normal'},
]

export const getMessageTypeMeta = (type?: string): MessageTypeMeta =>
    MESSAGE_TYPES.find((t) => t.value === type) ?? MESSAGE_TYPES[MESSAGE_TYPES.length - 1]

// A batch of drivers whose unread count grew during the last poll, i.e. genuinely NEW messages.
// Consumers (the stadium) react to `at` changing rather than to the array identity.
// The plate travels along with the driver id: a voyage with no assigned driver has no driver id on
// the board, and then the plate is the only thing left to match on.
export interface ChatIncoming {
    drivers: {driverId: number; plate?: string; routeId?: number}[]
    at: number
}

/** Plates are compared case- and space-insensitively; both sides are user-entered strings. */
export const normalizePlate = (plate?: string | null): string =>
    (plate ?? '').replace(/\s+/g, '').toUpperCase()

// A driver entry in the unread list (grouped by route).
export interface ChatDriverItem {
    driverId: number
    driverName?: string
    licensePlate?: string
    unreadCount?: number
    messageType?: string
    lastMessage?: {message?: string; date?: string | number; messageType?: string}
}

export interface ChatRouteGroup {
    routeId?: number
    routeName?: string
    drivers: ChatDriverItem[]
}

// A conversation in the "all chats" tab.
export interface ChatConversation {
    driverId: number
    driverName?: string
    licensePlate?: string
    routeId?: number
    routeName?: string
    unreadCount?: number
    lastMessage?: ChatMessage
}